/**
 * The HUD's mirror of game state (spec §9.1: React + Zustand for the HUD only).
 *
 * This store never computes anything physical. It holds what the HUD draws, fed
 * from the simulation worker once that pipeline lands in M1. Values the simulation
 * has not produced yet are `undefined` rather than zero, so the HUD can render a
 * dash instead of inventing a number (pillar P1: no fake water).
 */

import { create } from 'zustand';

/** Player-selected speed; mirrors `hydro_sim::world::Speed` (spec §3.3). */
export type Speed = 'paused' | 'realtime' | 'fast10' | 'fast60' | 'season';

/** Labels and multipliers for the speed control. */
export const SPEEDS: readonly {
  readonly id: Speed;
  readonly label: string;
  readonly title: string;
}[] = [
  { id: 'paused', label: 'Pause', title: 'Paused: build only' },
  { id: 'realtime', label: '1x', title: '1 game-second per second' },
  { id: 'fast10', label: '10x', title: '10 game-seconds per second' },
  { id: 'fast60', label: '60x', title: 'Up to 60 game-seconds per second' },
  { id: 'season', label: 'Season', title: 'Quasi-steady fast-forward' },
];

/** Live renderer statistics, measured in the front end. */
export interface RenderStats {
  /** Frames per second, smoothed. */
  readonly fps: number;
  /** Frame time in milliseconds, smoothed. */
  readonly frameMs: number;
  /** Camera altitude above the focus plane (m). */
  readonly altitudeM: number;
  /** Camera tilt above the horizon (deg). */
  readonly tiltDeg: number;
  /** Graphics backend in use. */
  readonly backend: string;
  /** Terrain vertices in the scene. */
  readonly vertexCount: number;
}

/** State the simulation owns. Undefined until the worker pipeline lands (M1). */
export interface SimState {
  /** Game day since the season started. */
  readonly day?: number;
  /** Funds available (spec §8). */
  readonly fundsUsd?: number;
  /** Aggregate WRSI across target fields, 0-100 (spec §5.6). */
  readonly wrsi?: number;
  /** Air temperature now (deg C). */
  readonly airTempC?: number;
  /** Surface water storage on the map (m³). */
  readonly storageM3?: number;
  /** True when the solver cannot keep up with the requested speed (spec §3.3). */
  readonly simLimited?: boolean;
}

/** One cell's inspector data; mirrors `hydro_sim::world::CellReport` (spec §6.2). */
export interface InspectedCell {
  readonly x: number;
  readonly y: number;
  readonly zM: number;
  readonly slope: number;
}

interface GameStore {
  readonly speed: Speed;
  readonly render: RenderStats;
  readonly sim: SimState;
  /** The cell shown in the Inspector, or `undefined` when nothing is selected. */
  readonly inspected: InspectedCell | undefined;
  setSpeed: (speed: Speed) => void;
  setRenderStats: (stats: Partial<RenderStats>) => void;
  setSim: (sim: Partial<SimState>) => void;
  inspect: (cell: InspectedCell | undefined) => void;
}

export const useGameStore = create<GameStore>((set) => ({
  speed: 'paused',
  inspected: undefined,
  render: {
    fps: 0,
    frameMs: 0,
    altitudeM: 0,
    tiltDeg: 0,
    backend: 'initialising',
    vertexCount: 0,
  },
  sim: {},
  setSpeed: (speed) => set({ speed }),
  setRenderStats: (stats) => set((s) => ({ render: { ...s.render, ...stats } })),
  setSim: (sim) => set((s) => ({ sim: { ...s.sim, ...sim } })),
  inspect: (inspected) => set({ inspected }),
}));
